import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { api } from "./api";
import logo from "../assets/logoVerde.svg";
import fondo from "../assets/esquina.jpg";
import ComputerIcon from "@mui/icons-material/Computer";
import { useTurnstile } from "../hooks/useTurnstile";
import { Visibility, VisibilityOff } from "@mui/icons-material";

const ForgotPassword = () => {
  const navigate = useNavigate();

  // Pasos: 1 = Email, 2 = Código, 3 = Cambiar contraseña
  const [step, setStep] = useState(1);

  const [email, setEmail] = useState("");
  const [codigo, setCodigo] = useState("");
  const [form, setForm] = useState({ nuevaContraseña: "", repetir: "" });

  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const [emailError, setEmailError] = useState("");
  const [codigoError, setCodigoError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const { containerRef, token, loading: captchaLoading, failover, reset } = useTurnstile();

  const showMessage = (message, severity = "info") =>
    setSnackbar({ open: true, message, severity });
  const handleCloseSnackbar = () =>
    setSnackbar({ ...snackbar, open: false });

  const renderButtonContent = (text) =>
    loading ? <CircularProgress size={24} color="inherit" /> : text;

  // --- Paso 1: Enviar email ---
  const handleSendEmail = async () => {
    setEmailError("");

    if (!email) {
      setEmailError("El email es obligatorio");
      return;
    }

    if (!token && !failover) {
      setEmailError("Debes completar el Captcha.");
      return;
    }

    setLoading(true);

    try {
      const bypass = failover ? true : false;

      const res = await api.auth.forgotPasswordEmail(
        email,
        token,
        bypass
      );

      if (res.error) {
        setEmailError(res.error);
        return;
      }

      showMessage("Código enviado a tu email", "success");
      setStep(2);
    } catch (err) {
       reset()
      setEmailError(err.error || "Error enviando email");
    } finally {
      setLoading(false);
    }
  };

  // --- Paso 2: Verificar código ---
  const handleVerifyCode = async () => {
    setCodigoError("");

    if (!codigo) {
      setCodigoError("Debes ingresar el código");
      return;
    }

    setLoading(true);
    try {
      const res = await api.auth.checkEmailVerified(email, codigo);

      if (!res.token) {
        setCodigoError("Código incorrecto");
        return;
      }

      localStorage.setItem("token", res.token.trim());
      showMessage("Código verificado correctamente", "success");
      setStep(3);
    } catch (err) {
      setCodigoError(err.error || "Error verificando código");
    } finally {
      setLoading(false);
    }
  };

  // --- Paso 3: Cambiar contraseña ---
  const handleChangePassword = async () => {
    setPasswordError("");

    if (!form.nuevaContraseña || !form.repetir) {
      setPasswordError("Ambos campos son obligatorios");
      return;
    }

    if (form.nuevaContraseña !== form.repetir) {
      setPasswordError("Las contraseñas no coinciden");
      return;
    }

    setLoading(true);
    try {
      const res = await api.usuario.updateUserPassword(form.nuevaContraseña);

      showMessage(
        res.message ||
          "Contraseña actualizada. Serás redirigido...",
        "success"
      );

      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      setPasswordError(err.error || "Error actualizando contraseña");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundImage: `url(${fondo})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          bgcolor: "rgba(0,0,0,0.4)",
          zIndex: 1,
        },
      }}
    >
      <Box
        position="relative"
        zIndex={2}
        width="100%"
        maxWidth={430}
        sx={{ m: { xs: 2, sm: 3 } }}
      >
        <Box
          sx={{
            backgroundColor: "white",
            padding: { xs: 3, sm: 4 },
            borderRadius: 2,
            boxShadow: 3,
            textAlign: "center",
          }}
        >
          <Box mb={1}>
            <img src={logo} alt="Logo" style={{ width: 80 }} />
          </Box>

          <Typography
            variant="subtitle1"
            mb={2}
            sx={{
              fontWeight: "bold",
              fontSize: { xs: 14, sm: 15 },
              lineHeight: 1.3,
            }}
          >
            Cooperativa de Servicios Públicos, Sociales y Vivienda de Avellaneda Ltda.
          </Typography>

          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            mb={2}
          >
            <ComputerIcon sx={{ color: "#6aae3b", mr: 1, fontSize: 26 }} />
            <Typography
              variant="h5"
              sx={{ color: "#6aae3b", fontWeight: 600 }}
            >
              Oficina Virtual
            </Typography>
          </Box>

          <Box
            sx={{
              width: 50,
              height: 2,
              backgroundColor: "#6aae3b",
              margin: "0 auto",
              borderRadius: 1,
              mb: 3,
            }}
          />

          {/* --- Paso 1: Email --- */}
          {step === 1 && (
            <>
              <Typography mb={2}>
                Ingresá tu email para recibir el código.
              </Typography>

              <TextField
                label="Email"
                fullWidth
                size="small"
                margin="dense"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setEmailError("");
                }}
                error={!!emailError}
                helperText={emailError}
                sx={{ "& .MuiInputBase-root": { height: 40 }, mb: 1 }}
              />

              {/* CAPTCHA — usando hook */}
              <Box display="flex" justifyContent="center" mb={2} minHeight={55}>
                {failover ? (
                  <Alert severity="warning">
                    No se pudo cargar el servicio de seguridad.
                  </Alert>
                ) : (
                  <div ref={containerRef} />
                )}
              </Box>

              <Button
                fullWidth
                variant="contained"
                onClick={handleSendEmail}
                disabled={(!token && !failover) || loading}
              >
                {renderButtonContent("Enviar código")}
              </Button>

              <Button
                fullWidth
                variant="text"
                sx={{ mt: 1 }}
                onClick={() => navigate("/login")}
              >
                Volver
              </Button>
            </>
          )}

          {/* --- Paso 2: Código --- */}
          {step === 2 && (
            <>
              <Typography mb={2}>
                Ingresá el código enviado a tu correo.
              </Typography>

              <TextField
                label="Código"
                fullWidth
                size="small"
                value={codigo}
                margin="dense"
                onChange={(e) => {
                  setCodigo(e.target.value);
                  setCodigoError("");
                }}
                error={!!codigoError}
                helperText={codigoError}
                sx={{ "& .MuiInputBase-root": { height: 40 }, mb: 2 }}
              />

              <Button
                fullWidth
                variant="contained"
                onClick={handleVerifyCode}
              >
                {renderButtonContent("Verificar código")}
              </Button>

              <Button fullWidth variant="text" sx={{ mt: 1 }} onClick={() => setStep(1)}>
                Atrás
              </Button>
            </>
          )}

          {/* --- Paso 3: Cambiar contraseña --- */}
          {step === 3 && (
            <>
              <Typography mb={2}>
                Ingresá tu nueva contraseña.
              </Typography>

              {/* <TextField
                fullWidth
                label="Nueva contraseña"
                type="password"
                size="small"
                margin="dense"
                value={form.nuevaContraseña}
                error={!!passwordError}
                onChange={(e) => {
                  setForm({ ...form, nuevaContraseña: e.target.value });
                  setPasswordError("");
                }}
                sx={{ "& .MuiInputBase-root": { height: 40 }, mb: 2 }}
              /> */}
              <TextField
                  fullWidth
                  label="Nueva contraseña"
                  type={showPassword ? "text" : "password"}
                  size="small"
                  margin="dense"
                  value={form.nuevaContraseña}
                  error={!!passwordError}
                  onChange={(e) => {
                    setForm({ ...form, nuevaContraseña: e.target.value });
                    setPasswordError("");
                  }}
                  sx={{ "& .MuiInputBase-root": { height: 40 }, mb: 2 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

{/* 
              <TextField
                fullWidth
                label="Repetir contraseña"
                type="password"
                size="small"
                margin="dense"
                value={form.repetir}
                error={!!passwordError}
                helperText={passwordError}
                onChange={(e) => {
                  setForm({ ...form, repetir: e.target.value });
                  setPasswordError("");
                }}
                sx={{ "& .MuiInputBase-root": { height: 40 }, mb: 2 }}
              /> */}
                <TextField
                  fullWidth
                  label="Repetir contraseña"
                  type={showPassword ? "text" : "password"}
                  size="small"
                  margin="dense"
                  value={form.repetir}
                  error={!!passwordError}
                  helperText={passwordError}
                  onChange={(e) => {
                    setForm({ ...form, repetir: e.target.value });
                    setPasswordError("");
                  }}
                  sx={{ "& .MuiInputBase-root": { height: 40 }, mb: 2 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

              <Button
                fullWidth
                variant="contained"
                onClick={handleChangePassword}
              >
                {renderButtonContent("Actualizar contraseña")}
              </Button>

              <Button fullWidth variant="text" onClick={() => navigate("/login")}>
                Cancelar
              </Button>
            </>
          )}
        </Box>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default ForgotPassword;
